import type { Page, Pagination } from '@aigc/types';
import {
  mutationOptions,
  queryOptions,
  skipToken,
} from '@tanstack/react-query';
import { http } from '../common/http-client';
import { getBaseTaskListQueryKey } from '../task/api';
import type { TaskDto } from '../task/model';
import type {
  TranslatePromptInputDto,
  TranslatePromptOutputDto,
} from '../video/multi-prompt-model';
import type {
  AdminVideoGenerationTaskListInputDto,
  GenerateVideoInputDto,
  OneVideoGenerationTaskInputDto,
  SmartEraseInputDto,
  VideoEditInputDto,
  VideoGenerationTaskListInputDto,
} from './model';

export const translatePrompt = (input: TranslatePromptInputDto) => {
  const data = http
    .post('script/segments/translate', {
      json: input,
    })
    .json<TranslatePromptOutputDto>();

  return data;
};

export const translatePromptMutationOptions = mutationOptions({
  mutationFn: async (input: TranslatePromptInputDto) => {
    return translatePrompt(input);
  },
});

export const getVideoGenerationTasksQueryOptions = (
  pagination: Pagination,
  params: VideoGenerationTaskListInputDto,
) => {
  return queryOptions({
    queryKey: [
      ...getBaseTaskListQueryKey(),
      'video/history',
      pagination,
      params,
    ],
    queryFn: () =>
      http
        .post('video/history', { json: { pagination, ...params } })
        .json<Page<TaskDto>>(),
  });
};

export const getAdminVideoGenerationTasksQueryOptions = (
  pagination: Pagination,
  params: AdminVideoGenerationTaskListInputDto,
) => {
  return queryOptions({
    queryKey: [
      ...getBaseTaskListQueryKey(),
      'statistics/video/history',
      pagination,
      params,
    ],
    queryFn: () =>
      http
        .post('statistics/video/history', { json: { pagination, ...params } })
        .json<Page<TaskDto>>(),
  });
};

export const getOneVideoGenerationTaskStatusQueryOptions = (
  query?: OneVideoGenerationTaskInputDto,
) => {
  return queryOptions({
    queryKey: ['任务', query],
    queryFn:
      query == null
        ? skipToken
        : async () => {
            const output = await http
              .post('video/task/status', {
                json: query,
              })
              .json<TaskDto>();

            return output;
          },
  });
};

export const queryManyVideoGenerationTaskStatus = async (
  query: OneVideoGenerationTaskInputDto[],
) => {
  const data = await http
    .post('video/task/status/batch', {
      json: {
        tasks: query,
      },
    })
    .json<{ data: TaskDto[] }>();

  return data.data;
};

export const getGenerateVideoTaskMutationOptions = () =>
  mutationOptions({
    mutationKey: [...getBaseTaskListQueryKey()],
    mutationFn: async (input: GenerateVideoInputDto) => {
      const output = await http
        .post('video/generate', {
          json: input,
        })
        .json<TaskDto>();

      return output;
    },
  });

export const smartEraseMutation = mutationOptions({
  mutationKey: [...getBaseTaskListQueryKey()],
  mutationFn: async (input: SmartEraseInputDto) => {
    const output = await http
      .post('video/smart-erase', {
        json: input,
      })
      .json<TaskDto>();

    return output;
  },
});

export const videoQualityRestorationMutation = mutationOptions({
  mutationKey: [...getBaseTaskListQueryKey()],
  mutationFn: async (input: { video_url: string }) => {
    const output = await http
      .post('tasks/video-quality-restoration', {
        json: input,
      })
      .json<TaskDto>();

    return output;
  },
});

export const getEditVideoMutationOptions = () =>
  mutationOptions({
    mutationKey: [...getBaseTaskListQueryKey()],
    mutationFn: async (input: VideoEditInputDto) => {
      const output = await http
        .post('video/edit', {
          json: input,
        })
        .json<TaskDto>();

      return output;
    },
  });
